"use client"

import { useState, useEffect } from "react"
import { Check, X, Clock, Sparkles } from "lucide-react"

const QuizQuestion = ({ quizData = [] }) => {
  const [questions, setQuestions] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [results, setResults] = useState([])
  const [timeLeft, setTimeLeft] = useState(60)
  const [quizFinished, setQuizFinished] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)

  useEffect(() => {
    if (quizData && quizData.length > 0) {
      setQuestions(quizData)
    }
  }, [quizData])

  useEffect(() => {
    if (timeLeft > 0 && currentIndex < questions.length && !showAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    } else if (timeLeft === 0 && currentIndex < questions.length) {
      handleNext()
    }
  }, [timeLeft, currentIndex, questions.length, showAnswer])

  const handleNext = () => {
    setSelectedIndex(null)
    setShowAnswer(false)
    setTimeLeft(60)

    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(currentIndex + 1)
    } else {
      setQuizFinished(true)
    }
  }

  const handleOptionSelect = (index) => {
    if (!showAnswer && selectedIndex === null) {
      setSelectedIndex(index)
      setShowAnswer(true)

      // Convert answer to a numeric index
      const correctIndex = parseInt(questions[currentIndex].answer, 10)

      // Add the result immediately
      setResults([
        ...results,
        {
          question: questions[currentIndex].question,
          selectedIndex: index,
          correctIndex,
          isCorrect: index === correctIndex,
        },
      ])

      // Automatically proceed to next question after 2 seconds
      setTimeout(() => {
        handleNext()
      }, 2000)
    }
  }

  if (quizFinished) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Sparkles className="text-green-500 animate-bounce" />
            <h3 className="text-lg font-semibold text-indigo-600">Quiz Summary</h3>
          </div>
        </div>
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2">
            <span className="text-4xl font-bold text-blue-600">{results.filter((r) => r.isCorrect).length}</span>
            <span className="text-2xl text-gray-600">/</span>
            <span className="text-4xl font-bold text-gray-600">{questions.length}</span>
          </div>
        </div>

        <div className="space-y-4 mt-8">
          {results.map((result, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border bg-white shadow-sm"
              style={{
                opacity: 1,
                transform: "none",
                transition: `all 0.3s ease ${index * 0.1}s`,
              }}
            >
              <h3 className="font-medium mb-2">{result.question}</h3>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Your answer:</span>
                  <span className={`font-medium ${result.isCorrect ? "text-green-600" : "text-red-600"}`}>
                    {questions[index].mcqOptions[result.selectedIndex] || "No answer"}
                  </span>
                </div>
                {!result.isCorrect && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Correct answer:</span>
                    <span className="font-medium text-green-600">
                      {questions[index].mcqOptions[result.correctIndex]}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <Sparkles className="text-green-500 animate-bounce" />
          <h3 className="text-lg font-semibold text-indigo-600">Quiz</h3>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-medium">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="h-4 w-4" />
            <span>{timeLeft}s</span>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
            style={{ width: `${(timeLeft / 60) * 100}%` }}
          />
        </div>
      </div>

      {questions[currentIndex] && (
        <div>
          <h2 className="text-xl font-semibold mb-6">{questions[currentIndex].question}</h2>
          <div className="space-y-3">
            {questions[currentIndex].mcqOptions.map((option, index) => (
              <button
                key={index}
                className={`w-full text-left py-4 px-6 rounded-lg border transition-all duration-200 ${
                  selectedIndex === index
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-200 hover:border-blue-600 hover:bg-blue-50"
                } ${
                  showAnswer && index === parseInt(questions[currentIndex].answer, 10)
                    ? "bg-green-100 border-green-500 text-green-700"
                    : showAnswer && selectedIndex === index && index !== parseInt(questions[currentIndex].answer, 10)
                      ? "bg-red-100 border-red-500 text-red-700"
                      : ""
                }`}
                onClick={() => handleOptionSelect(index)}
                disabled={showAnswer}
                style={{
                  opacity: 1,
                  transform: "none",
                  transition: `all 0.3s ease ${index * 0.1}s`,
                }}
              >
                <div className="flex items-center gap-3">
                  {showAnswer && index === parseInt(questions[currentIndex].answer, 10) && (
                    <Check className="h-5 w-5 text-green-600" />
                  )}
                  {showAnswer && selectedIndex === index && index !== parseInt(questions[currentIndex].answer, 10) && (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default QuizQuestion